import Phaser from 'phaser';
import { GAME_FONT_FAMILY } from '@shared/constants';
import { listFx } from '../particleEditor/catalogClient';
import type { ParticleEffectDefV2 } from '@shared/types/particles';

/**
 * ParticlePanel — pantalla in-game con listado de FX del catálogo.
 * Replica el patrón de AnimPanel: drag por título, lista de 10 filas
 * scrollable conceptual (paginable con flechas), botón Invocar que dispara
 * el FX seleccionado sobre el local player.
 *
 * Mientras F2 (motor v2 nativo) no esté implementada, el handler de invoke
 * en GameScene usa el motor v1 vía conversión v2→v1.
 */

function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return {
    fontFamily: GAME_FONT_FAMILY,
    resolution: 2,
    ...style,
  } as T;
}

type ParticlePanelCallbacks = {
  onMove(x: number, y: number): void;
  /** Devuelve qué motor terminó atendiendo el spawn ('v1' fallback / 'v2' nativo). */
  onPlayFx(fxId: string, def: ParticleEffectDefV2): string;
  /** Modo projectile: dispara FX desde local player hacia el punto clickeado. */
  onShootFx(fxId: string, def: ParticleEffectDefV2, worldX: number, worldY: number): string;
  /** F4: broadcast del aura al server. Server muta auraFxIds y todos los clientes lo ven. */
  onBroadcastAura(fxId: string): void;
  /** Limpia todos los FX activos sobre el local player (invocados + auras sync). */
  onClearAll(): void;
};

/** Filtros de categoría visibles en el dropdown del panel. */
const CATEGORY_FILTERS: Array<{ value: string; label: string; tagsAny: string[] }> = [
  { value: 'all', label: 'Todos', tagsAny: [] },
  { value: 'weapon', label: '⚔️ Weapon', tagsAny: ['weapon'] },
  { value: 'aura', label: '🌀 Auras', tagsAny: ['aura'] },
  { value: 'impact', label: '💥 Impactos', tagsAny: ['impact'] },
  { value: 'projectile', label: '🎯 Projectiles', tagsAny: ['projectile'] },
  { value: 'cast', label: '✨ Cast', tagsAny: ['cast'] },
  { value: 'meditate', label: '🧘 Meditación', tagsAny: ['meditate'] },
  { value: 'overlay', label: '🌫️ Overlay', tagsAny: ['overlay'] },
  { value: 'debuff', label: '☠️ Debuff', tagsAny: ['debuff', 'dot'] },
];

type ParticlePanelOptions = {
  initialX: number;
  initialY: number;
};

type ParticlePanelElements = {
  bg: Phaser.GameObjects.Rectangle;
  title: Phaser.GameObjects.Text;
  list: Phaser.GameObjects.Text[];
  invokeBtn: Phaser.GameObjects.Text;
  shootBtn: Phaser.GameObjects.Text;
  broadcastBtn: Phaser.GameObjects.Text;
  refreshBtn: Phaser.GameObjects.Text;
  prevBtn: Phaser.GameObjects.Text;
  nextBtn: Phaser.GameObjects.Text;
  pageLabel: Phaser.GameObjects.Text;
  closeButton: Phaser.GameObjects.Text;
  clearBtn: Phaser.GameObjects.Text;
  filterBtn: Phaser.GameObjects.Text;
  emptyMsg: Phaser.GameObjects.Text;
  motorBadge: Phaser.GameObjects.Text;
  panelX: number;
  panelY: number;
  panelWidth: number;
  panelHeight: number;
};

const ROWS_PER_PAGE = 10;

export class ParticlePanel {
  private readonly scene: Phaser.Scene;
  private readonly callbacks: ParticlePanelCallbacks;
  private panel: ParticlePanelElements | null = null;
  private isOpen = false;
  private isDragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  private catalog: Record<string, ParticleEffectDefV2> = {};
  private fxIds: string[] = [];
  private selectedIndex = -1; // índice ABSOLUTO en fxIds (post-filtro)
  private page = 0;
  /** Categoría activa del filtro (clave de CATEGORY_FILTERS). */
  private filterIdx = 0;
  /** Cuando true, el siguiente click en el mundo dispara el FX seleccionado. */
  private shootMode = false;
  private shootListener: ((pointer: Phaser.Input.Pointer) => void) | null = null;

  constructor(scene: Phaser.Scene, callbacks: ParticlePanelCallbacks, options: ParticlePanelOptions) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.buildPanel(options.initialX, options.initialY);
    this.setVisible(false);
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    this.setVisible(this.isOpen);
    if (this.isOpen) void this.refreshCatalog();
  }

  open(): void {
    this.isOpen = true;
    this.setVisible(true);
    void this.refreshCatalog();
  }

  close(): void {
    this.isOpen = false;
    this.setVisible(false);
  }

  isOpenPanel(): boolean {
    return this.isOpen;
  }

  handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.isDragging || !this.panel) return;
    const newX = pointer.x - this.dragOffsetX;
    const newY = pointer.y - this.dragOffsetY;
    this.move(newX, newY);
    this.callbacks.onMove(newX, newY);
  }

  handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.isDragging) {
      this.isDragging = false;
      pointer.event.stopPropagation();
    }
  }

  isDraggingPanel(): boolean {
    return this.isDragging;
  }

  isPointerOver(pointer: Phaser.Input.Pointer): boolean {
    if (!this.panel || !this.isOpen) return false;
    const { panelX, panelY, panelWidth, panelHeight } = this.panel;
    const left = panelX - panelWidth / 2;
    const right = panelX + panelWidth / 2;
    const top = panelY - panelHeight / 2;
    const bottom = panelY + panelHeight / 2;
    return pointer.x >= left && pointer.x <= right && pointer.y >= top && pointer.y <= bottom;
  }

  // ── Build ──────────────────────────────────────────────────────────────────

  private buildPanel(initialX: number, initialY: number): void {
    const panelWidth = 320;
    const panelHeight = 320;
    const panelX = initialX;
    const panelY = initialY;

    const bg = this.scene.add
      .rectangle(panelX, panelY, panelWidth, panelHeight, 0x111111, 0.92)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x666666)
      .setDepth(1100)
      .setScrollFactor(0)
      // Fondo interactivo: absorbe hover/click de objetos del mundo debajo
      // (Phaser topOnly) para que la ventana no deje pasar clicks ni cambie el cursor.
      .setInteractive()
      .setVisible(false);

    const baseX = panelX - panelWidth / 2;
    const baseY = panelY - panelHeight / 2;

    const title = this.scene.add
      .text(panelX, baseY + 16, 'Partículas', withGameFont({ fontSize: '16px', color: '#ffaaff' }))
      .setOrigin(0.5, 0.5)
      .setDepth(1100)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });

    title.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragOffsetX = pointer.x - bg.x;
      this.dragOffsetY = pointer.y - bg.y;
      pointer.event.stopPropagation();
    });

    const refreshBtn = this.scene.add
      .text(baseX + 16, baseY + 8, '↻', withGameFont({
        fontSize: '14px', color: '#ffffff', backgroundColor: '#444',
      }))
      .setOrigin(0, 0)
      .setPadding(4, 2, 4, 2)
      .setInteractive({ useHandCursor: true })
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    refreshBtn.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      void this.refreshCatalog();
      pointer.event.stopPropagation();
    });

    // Filtro por categoría (click para ciclar entre tags)
    const filterBtn = this.scene.add
      .text(baseX + 50, baseY + 8, CATEGORY_FILTERS[0].label, withGameFont({
        fontSize: '11px', color: '#cbd5e1', backgroundColor: '#1a4a8e',
      }))
      .setOrigin(0, 0)
      .setPadding(5, 3, 5, 3)
      .setInteractive({ useHandCursor: true })
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    filterBtn.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.cycleFilter();
      pointer.event.stopPropagation();
    });

    // Clear all — saca todas las partículas activas del local player
    const clearBtn = this.scene.add
      .text(panelX + panelWidth / 2 - 50, baseY + 8, '🧹 Clear', withGameFont({
        fontSize: '11px', color: '#fed7aa', backgroundColor: '#7c2d12',
      }))
      .setOrigin(1, 0)
      .setPadding(5, 3, 5, 3)
      .setInteractive({ useHandCursor: true })
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    clearBtn.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.callbacks.onClearAll();
      if (this.panel) {
        this.panel.motorBadge.setText('🧹 FX limpios').setColor('#fed7aa').setVisible(true);
      }
      pointer.event.stopPropagation();
    });

    const closeButton = this.scene.add
      .text(panelX + panelWidth / 2 - 16, baseY + 8, '✕', withGameFont({
        fontSize: '14px', color: '#ffffff', backgroundColor: '#555',
      }))
      .setOrigin(1, 0)
      .setPadding(4, 2, 4, 2)
      .setInteractive({ useHandCursor: true })
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    closeButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.close();
      pointer.event.stopPropagation();
    });

    // Lista de FX (10 filas)
    const listStartY = baseY + 40;
    const rowHeight = 20;
    const list: Phaser.GameObjects.Text[] = [];

    for (let i = 0; i < ROWS_PER_PAGE; i++) {
      const y = listStartY + i * rowHeight;
      const txt = this.scene.add
        .text(baseX + 12, y, '', withGameFont({ fontSize: '12px', color: '#ffffff' }))
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true })
        .setDepth(1101)
        .setScrollFactor(0)
        .setVisible(false);

      const rowIdx = i;
      txt.on('pointerup', (pointer: Phaser.Input.Pointer) => {
        this.handleRowClick(rowIdx);
        pointer.event.stopPropagation();
      });
      txt.on('pointerover', () => txt.setStyle({ backgroundColor: '#2a2a4a' }));
      txt.on('pointerout', () => txt.setStyle({ backgroundColor: '' }));
      list.push(txt);
    }

    // Empty msg (cuando no hay FX en el catálogo)
    const emptyMsg = this.scene.add
      .text(panelX, listStartY + 60, '(catálogo vacío)\nCargá FX desde el Particle Editor', withGameFont({
        fontSize: '11px', color: '#888888', align: 'center',
      }))
      .setOrigin(0.5, 0)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    // Paginación
    const pagerY = panelY + panelHeight / 2 - 50;
    const prevBtn = this.scene.add
      .text(panelX - 60, pagerY, '◀', withGameFont({
        fontSize: '14px', color: '#ffffff', backgroundColor: '#333',
      }))
      .setOrigin(0.5)
      .setPadding(6, 2, 6, 2)
      .setInteractive({ useHandCursor: true })
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);
    prevBtn.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (this.page > 0) {
        this.page -= 1;
        this.refreshList();
      }
      pointer.event.stopPropagation();
    });

    const pageLabel = this.scene.add
      .text(panelX, pagerY, '1 / 1', withGameFont({ fontSize: '11px', color: '#cccccc' }))
      .setOrigin(0.5)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    const nextBtn = this.scene.add
      .text(panelX + 60, pagerY, '▶', withGameFont({
        fontSize: '14px', color: '#ffffff', backgroundColor: '#333',
      }))
      .setOrigin(0.5)
      .setPadding(6, 2, 6, 2)
      .setInteractive({ useHandCursor: true })
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);
    nextBtn.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      const maxPage = Math.max(0, Math.ceil(this.fxIds.length / ROWS_PER_PAGE) - 1);
      if (this.page < maxPage) {
        this.page += 1;
        this.refreshList();
      }
      pointer.event.stopPropagation();
    });

    // Botón Invocar (sobre el local player — solo cliente)
    const invokeBtn = this.scene.add
      .text(panelX - 75, panelY + panelHeight / 2 - 24, 'Invocar', withGameFont({
        fontSize: '12px', color: '#aaffaa', backgroundColor: '#225522',
      }))
      .setOrigin(0.5)
      .setPadding(6, 4, 6, 4)
      .setInteractive({ useHandCursor: true })
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    invokeBtn.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.handleInvoke();
      pointer.event.stopPropagation();
    });

    // Botón Shoot (projectile: click siguiente define destino — solo cliente)
    const shootBtn = this.scene.add
      .text(panelX, panelY + panelHeight / 2 - 24, '🎯 Shoot', withGameFont({
        fontSize: '12px', color: '#ffaa66', backgroundColor: '#552222',
      }))
      .setOrigin(0.5)
      .setPadding(6, 4, 6, 4)
      .setInteractive({ useHandCursor: true })
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    shootBtn.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.toggleShootMode();
      pointer.event.stopPropagation();
    });

    // Botón Broadcast (F4: toggle aura via server — visible para TODOS los clientes)
    const broadcastBtn = this.scene.add
      .text(panelX + 75, panelY + panelHeight / 2 - 24, '📡 Aura', withGameFont({
        fontSize: '12px', color: '#aaccff', backgroundColor: '#223355',
      }))
      .setOrigin(0.5)
      .setPadding(6, 4, 6, 4)
      .setInteractive({ useHandCursor: true })
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    broadcastBtn.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.handleBroadcast();
      pointer.event.stopPropagation();
    });

    // Motor badge — muestra v1/v2 del último spawn (debajo del botón invocar)
    const motorBadge = this.scene.add
      .text(panelX, panelY + panelHeight / 2 - 8, '', withGameFont({
        fontSize: '10px', color: '#888',
      }))
      .setOrigin(0.5)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    this.panel = {
      bg, title, list, invokeBtn, shootBtn, broadcastBtn, refreshBtn, prevBtn, nextBtn, pageLabel,
      closeButton, clearBtn, filterBtn, emptyMsg, motorBadge, panelX, panelY, panelWidth, panelHeight,
    };
  }

  // ── Filtro ────────────────────────────────────────────────────────────────

  private cycleFilter(): void {
    this.filterIdx = (this.filterIdx + 1) % CATEGORY_FILTERS.length;
    if (this.panel) {
      this.panel.filterBtn.setText(CATEGORY_FILTERS[this.filterIdx].label);
    }
    this.selectedIndex = -1;
    this.page = 0;
    this.refreshList();
  }

  /** IDs filtrados según la categoría activa. */
  private getFilteredIds(): string[] {
    const filter = CATEGORY_FILTERS[this.filterIdx];
    if (filter.tagsAny.length === 0) return this.fxIds;
    return this.fxIds.filter((id) => {
      const tags = this.catalog[id]?.tags ?? [];
      return filter.tagsAny.some((t) => tags.includes(t));
    });
  }

  // ── Broadcast (F4) ─────────────────────────────────────────────────────────

  private handleBroadcast(): void {
    const filtered = this.getFilteredIds();
    if (this.selectedIndex < 0 || this.selectedIndex >= filtered.length) {
      this.panel?.motorBadge.setText('Elegí un FX primero').setColor('#ff8888').setVisible(true);
      return;
    }
    const id = filtered[this.selectedIndex];
    this.callbacks.onBroadcastAura(id);
    if (this.panel) {
      this.panel.motorBadge
        .setText(`📡 Toggle "${id}" → server`)
        .setColor('#aaccff')
        .setVisible(true);
    }
  }

  // ── Shoot mode ─────────────────────────────────────────────────────────────

  private toggleShootMode(): void {
    if (this.selectedIndex < 0) {
      this.panel?.motorBadge.setText('Elegí un FX primero').setColor('#ff8888').setVisible(true);
      return;
    }
    if (this.shootMode) {
      this.exitShootMode();
    } else {
      this.enterShootMode();
    }
  }

  private enterShootMode(): void {
    this.shootMode = true;
    if (this.panel) {
      this.panel.shootBtn.setText('🎯 Click destino...').setColor('#ffff00');
      this.panel.motorBadge.setText('Click en el mapa para disparar').setColor('#ffaa66').setVisible(true);
    }
    this.shootListener = (pointer: Phaser.Input.Pointer): void => {
      // Ignorar clicks sobre el panel
      if (this.isPointerOver(pointer)) return;
      const filtered = this.getFilteredIds();
      const id = filtered[this.selectedIndex];
      const def = id ? this.catalog[id] : undefined;
      if (!def) {
        this.exitShootMode();
        return;
      }
      const worldX = pointer.worldX;
      const worldY = pointer.worldY;
      const motor = this.callbacks.onShootFx(id, def, worldX, worldY);
      if (this.panel) {
        this.panel.motorBadge.setText(
          motor === 'v2' ? '⚡ shoot v2' : '🐢 shoot v1'
        ).setColor(motor === 'v2' ? '#88ff88' : '#ffaa66');
      }
      this.exitShootMode();
    };
    this.scene.input.once('pointerdown', this.shootListener);
  }

  private exitShootMode(): void {
    this.shootMode = false;
    if (this.shootListener) {
      this.scene.input.off('pointerdown', this.shootListener);
      this.shootListener = null;
    }
    if (this.panel) {
      this.panel.shootBtn.setText('🎯 Shoot').setColor('#ffaa66');
    }
  }

  // ── Catalog fetch + render ─────────────────────────────────────────────────

  private async refreshCatalog(): Promise<void> {
    try {
      this.catalog = await listFx();
      this.fxIds = Object.keys(this.catalog).sort();
      // Mantener selectedIndex válido tras refresh
      if (this.selectedIndex >= this.fxIds.length) this.selectedIndex = -1;
      // Mantener page válida
      const maxPage = Math.max(0, Math.ceil(this.fxIds.length / ROWS_PER_PAGE) - 1);
      if (this.page > maxPage) this.page = maxPage;
      this.refreshList();
    } catch (err) {
      console.warn('[ParticlePanel] catálogo no disponible:', (err as Error).message);
      this.fxIds = [];
      this.refreshList();
    }
  }

  private refreshList(): void {
    if (!this.panel) return;
    const filtered = this.getFilteredIds();
    const start = this.page * ROWS_PER_PAGE;
    const end = Math.min(start + ROWS_PER_PAGE, filtered.length);
    const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));

    this.panel.pageLabel.setText(`${this.page + 1} / ${totalPages}`);
    this.panel.emptyMsg.setVisible(this.isOpen && filtered.length === 0);

    for (let i = 0; i < this.panel.list.length; i++) {
      const txt = this.panel.list[i];
      const absIdx = start + i;
      if (absIdx < end) {
        const id = filtered[absIdx];
        const def = this.catalog[id];
        const tags = def.tags && def.tags.length > 0 ? ` [${def.tags[0]}]` : '';
        txt.setText(`${absIdx + 1}. ${id}${tags}`);
        txt.setColor(absIdx === this.selectedIndex ? '#ffff66' : '#ffffff');
        txt.setVisible(this.isOpen);
      } else {
        txt.setText('');
        txt.setVisible(false);
      }
    }
  }

  private handleRowClick(row: number): void {
    const filtered = this.getFilteredIds();
    const absIdx = this.page * ROWS_PER_PAGE + row;
    if (absIdx >= filtered.length) return;
    this.selectedIndex = absIdx;
    this.refreshList();
  }

  private handleInvoke(): void {
    const filtered = this.getFilteredIds();
    if (this.selectedIndex < 0 || this.selectedIndex >= filtered.length) return;
    const id = filtered[this.selectedIndex];
    const def = this.catalog[id];
    if (!def) return;
    const motor = this.callbacks.onPlayFx(id, def);
    if (this.panel) {
      this.panel.motorBadge.setText(
        motor === 'v2' ? '⚡ motor v2 nativo (batching)' : '🐢 motor v1 fallback'
      );
      this.panel.motorBadge.setColor(motor === 'v2' ? '#88ff88' : '#ffaa66');
      this.panel.motorBadge.setVisible(true);
    }
  }

  // ── Visibility / move ──────────────────────────────────────────────────────

  private setVisible(visible: boolean): void {
    if (!this.panel) return;
    const p = this.panel;
    p.bg.setVisible(visible).setActive(visible);
    p.title.setVisible(visible).setActive(visible);
    p.refreshBtn.setVisible(visible).setActive(visible);
    p.filterBtn.setVisible(visible).setActive(visible);
    p.clearBtn.setVisible(visible).setActive(visible);
    p.closeButton.setVisible(visible).setActive(visible);
    p.invokeBtn.setVisible(visible).setActive(visible);
    p.shootBtn.setVisible(visible).setActive(visible);
    p.broadcastBtn.setVisible(visible).setActive(visible);
    p.prevBtn.setVisible(visible).setActive(visible);
    p.nextBtn.setVisible(visible).setActive(visible);
    p.pageLabel.setVisible(visible).setActive(visible);
    p.list.forEach((t) => t.setVisible(visible).setActive(visible));
    p.emptyMsg.setVisible(visible && this.fxIds.length === 0);
    p.motorBadge.setVisible(visible && p.motorBadge.text !== '');
    if (visible) this.refreshList();
  }

  private move(newX: number, newY: number): void {
    if (!this.panel) return;
    const p = this.panel;
    p.panelX = newX;
    p.panelY = newY;
    const baseX = newX - p.panelWidth / 2;
    const baseY = newY - p.panelHeight / 2;
    const listStartY = baseY + 40;
    const rowHeight = 20;
    const pagerY = newY + p.panelHeight / 2 - 50;

    p.bg.setPosition(newX, newY);
    p.title.setPosition(newX, baseY + 16);
    p.refreshBtn.setPosition(baseX + 16, baseY + 8);
    p.filterBtn.setPosition(baseX + 50, baseY + 8);
    p.clearBtn.setPosition(newX + p.panelWidth / 2 - 50, baseY + 8);
    p.closeButton.setPosition(newX + p.panelWidth / 2 - 16, baseY + 8);
    p.list.forEach((t, i) => t.setPosition(baseX + 12, listStartY + i * rowHeight));
    p.emptyMsg.setPosition(newX, listStartY + 60);
    p.prevBtn.setPosition(newX - 60, pagerY);
    p.pageLabel.setPosition(newX, pagerY);
    p.nextBtn.setPosition(newX + 60, pagerY);
    p.invokeBtn.setPosition(newX - 75, newY + p.panelHeight / 2 - 24);
    p.shootBtn.setPosition(newX, newY + p.panelHeight / 2 - 24);
    p.broadcastBtn.setPosition(newX + 75, newY + p.panelHeight / 2 - 24);
    p.motorBadge.setPosition(newX, newY + p.panelHeight / 2 - 8);
  }
}
